/** redeploy comment 1
=========================================================
* Material Dashboard 2 PRO React - v2.1.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-pro-react
* Copyright 2022 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

/** 
  All of the routes for the Material Dashboard 2 PRO React are added here,
  You can add a new route, customize the routes and delete the routes here.

  Once you add a new route on this file it will be visible automatically on
  the Sidenav.

  For adding a new route you can follow the existing routes in the routes array.
  1. The `type` key with the `collapse` value is used for a route.
  2. The `type` key with the `title` value is used for a title inside the Sidenav. 
  3. The `type` key with the `divider` value is used for a divider between Sidenav items.
  4. The `name` key is used for the name of the route on the Sidenav.
  5. The `key` key is used for the key of the route (It will help you with the key prop inside a loop).
  6. The `icon` key is used for the icon of the route on the Sidenav, you have to add a node.
  7. The `collapse` key is used for making a collapsible item on the Sidenav that contains other routes
  inside (nested routes), you need to pass the nested routes inside an array as a value for the `collapse` key.
  8. The `route` key is used to store the route location which is used for the react router.
  9. The `href` key is used to store the external links location.
  10. The `title` key is only for the item with the type of `title` and its used for the title text on the Sidenav.
  10. The `component` key is used to store the component of its route.
*/

// Material Dashboard 2 PRO React layouts
import NewUser from 'layouts/user-management/new-user';

import SignInBasic from 'layouts/authentication/sign-in/basic';

// Material Dashboard 2 PRO React components

// @mui icons
import Icon from '@mui/material/Icon';

// Images
import UserManagement from 'layouts/user-management';
import Dashboard from 'layouts/dashboard';
import EditUser from 'layouts/user-management/edit-user';
import Logout from 'layouts/authentication/logout';
import ShowUser from 'layouts/user-management/show-user';
import ClientManagement from 'layouts/client-management';
import Games from 'layouts/games';
import Players from 'layouts/players';
import Sessions from 'layouts/sessions';
import Metrics from 'layouts/metrics';
import EditClient from 'layouts/client-management/edit-client';
import Settings from 'layouts/pages/account/settings';
import Game from 'layouts/game';
import BrandManagement from 'layouts/brand-management';
import NewClient from 'layouts/operator-management/new-operator';
import OperatorManagement from 'layouts/operator-management';
import NewBrand from 'layouts/brand-management/new-brand';
import NewOperator from 'layouts/operator-management/new-operator';
import Suspicious from 'layouts/suspicious';
import ShowSuspicious from 'layouts/suspicious/show';
import FinancialReport from 'layouts/financial-report';
import Tournament from 'layouts/tournament';
import LeaderboardEntries from 'layouts/leaderboardEntries';
import CostManagement from 'layouts/cost-management';
import NHMetrics from 'layouts/nh-metrics';
import Leaderboards from 'layouts/leaderboards';
import LeaderboardDetails from 'layouts/leaderboards/show';
import PvPDashboard from 'layouts/pvp-dashboard';
import SlotsDashboard from 'layouts/slots-dashboard';
import MultiplayerLiquidityDashboard from 'layouts/multiplayer-liquidity-dashboard';
import ProviderConfig from 'layouts/client-management/provider-config';
import NHRoomList from 'layouts/nh-room-list';
import NHRoomDashboard from 'layouts/nh-room-dashboard';
import NHSessionHistory from 'layouts/nh-session-history';
import NHSessionDetail from 'layouts/nh-session-detail';

const routes = [
  { type: 'title', title: 'Dashboards', key: 'title-dashboards' },
  {
    type: 'collapse',
    name: 'Dashboards',
    key: 'dashboards',
    icon: <Icon fontSize='medium'>dashboard</Icon>,
    collapse: [
      {
        name: 'Main Dashboard',
        key: 'dashboard',
        icon: <Icon fontSize='medium'>home</Icon>,
        route: '/dashboard',
        component: <Dashboard />
      },
      {
        name: 'PvP Dashboard',
        key: 'pvp-dashboard',
        icon: <Icon fontSize='medium'>sports_esports</Icon>,
        route: '/pvp-dashboard',
        component: <PvPDashboard />
      },
      {
        name: 'Slots Dashboard',
        key: 'slots-dashboard',
        icon: <Icon fontSize='medium'>casino</Icon>,
        route: '/slots-dashboard',
        component: <SlotsDashboard />
      },
      {
        name: 'Multiplayer Liquidity',
        key: 'multiplayer-liquidity',
        icon: <Icon fontSize='medium'>groups</Icon>,
        route: '/multiplayer-liquidity',
        component: <MultiplayerLiquidityDashboard />
      },
      {
        name: 'Crypto Express Live',
        key: 'nh-rooms',
        icon: <Icon fontSize='medium'>train</Icon>,
        route: '/nh-rooms',
        component: <NHRoomList />
      },
      {
        name: 'Crypto Express History',
        key: 'nh-sessions',
        icon: <Icon fontSize='medium'>history</Icon>,
        route: '/nh-sessions',
        component: <NHSessionHistory />
      }
    ]
  },
  { type: 'title', title: 'Sections', key: 'title-sections' },
  {
    type: 'collapse',
    name: 'Admin',
    key: 'admin-management',
    icon: <Icon fontSize='medium'>manageAccounts</Icon>,
    collapse: [
      {
        name: 'Users',
        key: 'user-management',
        action: 'read',
        object: 'user',
        icon: <Icon fontSize='medium'>people</Icon>,
        route: '/user-management',
        component: <UserManagement />
      },
      {
        name: 'Integrations',
        key: 'client-management',
        action: 'read',
        object: 'client',
        icon: <Icon fontSize='medium'>business</Icon>,
        route: '/client-management',
        component: <ClientManagement />
      },
      {
        name: 'Operators',
        key: 'operator-management',
        action: 'read',
        object: 'operator',
        icon: <Icon fontSize='medium'>person</Icon>,
        route: '/operator-management',
        component: <OperatorManagement />
      },
      {
        name: 'Brands',
        key: 'brand-management',
        action: 'read',
        object: 'brand',
        icon: <Icon fontSize='medium'>person</Icon>,
        route: '/brand-management',
        component: <BrandManagement />
      },
      {
        name: 'Costs',
        key: 'cost-management',
        action: 'read',
        object: 'cost-report',
        icon: <Icon fontSize='medium'>$</Icon>,
        route: '/cost-management',
        component: <CostManagement />
      }
    ]
  },
  {
    type: 'collapse',
    noCollapse: true,
    icon: <Icon fontSize='medium'>casino</Icon>,
    name: 'Games',
    key: 'games',
    route: '/games',
    component: <Games />
  },
  {
    type: 'collapse',
    noCollapse: true,
    icon: <Icon fontSize='medium'>assessment</Icon>,
    name: 'Sessions',
    key: 'sessions',
    route: '/sessions',
    component: <Sessions />
  },
  {
    type: 'collapse',
    noCollapse: true,
    icon: <Icon fontSize='medium'>analytics</Icon>,
    name: 'Metrics',
    key: 'metrics',
    route: '/metrics',
    component: <Metrics />
  },
  {
    type: 'collapse',
    noCollapse: true,
    icon: <Icon fontSize='medium'>emoji_events</Icon>,
    name: 'Leaderboards',
    key: 'leaderboards',
    route: '/leaderboards',
    component: <Leaderboards />
  },
  {
    type: 'collapse',
    noCollapse: true,
    icon: <Icon fontSize='medium'>face</Icon>,
    name: 'Players',
    key: 'players',
    route: '/players',
    component: <Players />
  },
  {
    name: 'Sign In',
    key: 'sign-in',
    route: '/authentication/sign-in/basic',
    component: <SignInBasic />
  },
  {
    name: 'New User',
    key: 'new-user',
    route: '/user-management/new-user',
    component: <NewUser />
  },
  {
    name: 'New Integration',
    key: 'new-client',
    route: '/client-management/new-client',
    component: <NewClient />
  },
  {
    name: 'New Brand',
    key: 'new-brand',
    route: '/brand-management/new-brand',
    component: <NewBrand />
  },
  {
    name: 'New Operator',
    key: 'new-operator',
    route: '/operator-management/new-operator',
    component: <NewOperator />
  },
  {
    name: 'Show User',
    key: 'show-user',
    route: '/user-management/show/:id',
    component: <ShowUser />
  },
  {
    name: 'Edit User',
    key: 'edit-user',
    route: '/user-management/edit/:id',
    component: <EditUser />
  },
  {
    type: 'collapse',
    name: 'Settings',
    key: 'settings',
    icon: <Icon fontSize='medium'>settings</Icon>,
    collapse: [
      {
        name: 'Edit user',
        key: 'edit user',
        icon: <Icon fontSize='medium'>edit</Icon>,
        route: '/settings/user',
        component: <Settings />
      }
    ]
  },
  {
    type: 'collapse',
    noCollapse: true,
    icon: <Icon fontSize='medium'>block</Icon>,
    name: 'Suspicious actions',
    key: 'suspicious',
    action: 'read',
    object: 'suspicious',
    route: '/suspicious/',
    component: <Suspicious />
  },
  {
    type: 'collapse',
    noCollapse: true,
    icon: <Icon fontSize='medium'>analytics</Icon>,
    name: 'Financial report',
    key: 'fin-report',
    action: 'read',
    object: 'fin-report',
    route: '/fin-report/',
    component: <FinancialReport />
  },

  {
    name: 'Show Suspicious',
    key: 'show-suspicious',
    route: '/suspicious/show/:id',
    component: <ShowSuspicious />
  },

  {
    type: 'collapse',
    noCollapse: true,
    icon: <Icon fontSize='medium'>logout</Icon>,
    name: 'Logout',
    key: 'logout',
    route: '/authentication/logout',
    component: <Logout />
  },
  {
    name: 'Edit Integration',
    key: 'edit-client',
    route: '/client-management/edit/:id',
    component: <EditClient />
  },
  {
    name: 'Provider Config',
    key: 'provider-config',
    route: '/client-management/provider-config/:id',
    component: <ProviderConfig />
  },
  {
    name: 'Game',
    key: 'game',
    route: '/game/:id',
    component: <Game />
  },
  {
    name: 'Tournament',
    key: 'tournament',
    route: '/tournament/:id',
    component: <Tournament />
  },
  {
    name: 'Leaderboard Entries',
    key: 'leaderboard-entries',
    route: '/tournament/leaderboard/:id',
    component: <LeaderboardEntries />
  },
  {
    name: 'Leaderboard Details',
    key: 'leaderboards-show',
    route: '/leaderboards/:id',
    component: <LeaderboardDetails />
  },
  {
    name: 'NH Metrics',
    key: 'nh-metrics',
    route: '/nh-metrics',
    component: <NHMetrics />
  },
  {
    name: 'NH Metrics by Bet Limit',
    key: 'nh-metrics-bet-limit',
    route: '/nh-metrics/bet-limit/:betLimitId',
    component: <NHMetrics />
  },
  {
    name: 'NH Room Dashboard',
    key: 'nh-room-dashboard',
    route: '/nh-room/:roomId',
    component: <NHRoomDashboard />
  },
  {
    name: 'NH Session Detail',
    key: 'nh-session-detail',
    route: '/nh-session/:snapshotId',
    component: <NHSessionDetail />
  }
];

export default routes;

